import { cn } from '@lib/utils';
import * as React from 'react';

export type InputProps = React.ComponentProps<'input'> & {
  generateButton?: React.ReactNode;
  hasError?: boolean;
};

/**
 * Password-manager autofill is opted-out by default. Browsers and 1Password / LastPass /
 * Bitwarden / etc. should NOT offer to save or autofill these inputs unless the consumer
 * explicitly overrides `autoComplete` (and the matching `data-*-ignore` flag).
 */
const AUTOFILL_IGNORE_DEFAULTS = {
  autoComplete: 'off',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
  'data-lpignore': 'true',
} as const;

const Input = (props: InputProps) => {
  const { className, generateButton, hasError, type, ...otherProps } = props;
  const ariaInvalid = hasError || otherProps['aria-invalid'];
  const autofillDefaults = type === 'password' ? {} : AUTOFILL_IGNORE_DEFAULTS;

  return (
    <div className="relative w-full">
      <input
        aria-invalid={ariaInvalid}
        className={cn(
          'h-10 w-full rounded-md bg-gray-100 px-3 py-2 text-base text-gray-600',
          'border border-gray-200 box-border md:text-sm placeholder:text-gray-400',
          'focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
          'aria-invalid:border-red-500 aria-invalid:focus-visible:border-red-500 aria-invalid:focus-visible:ring-red-200',
          'disabled:cursor-not-allowed disabled:opacity-50',
          type === 'file' &&
            'cursor-pointer px-0 py-0 file:mr-2 file:h-6.5 file:mt-1.5 file:ml-1.5 file:cursor-pointer file:rounded-md file:border-0 file:bg-gray-200 file:border file:border-gray-300 file:px-3 file:text-xs file:font-normal file:text-gray-500',
          className,
          type !== 'file' && generateButton && 'pr-8',
        )}
        type={type}
        {...autofillDefaults}
        {...otherProps}
      />
      {generateButton && <div className="absolute inset-y-0 right-1.5 flex items-center">{generateButton}</div>}
    </div>
  );
};
Input.displayName = 'Input';

export { Input };
